import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import ValueProposition from './components/ValueProposition';
import AIReadinessWidget from './components/AIReadinessWidget';
import ClientMetrics from './components/ClientMetrics';
import RoleBasedNavigation from './components/RoleBasedNavigation';
import SocialProof from './components/SocialProof';

const Homepage = () => {
  return (
    <>
      <Helmet>
        <title>AI Clean Core - Enterprise AI Independence | Open-Source AI Strategy</title>
        <meta name="description" content="Transform your organization with AI independence. Build internal AI capabilities, eliminate vendor lock-in, and future-proof your business with open-source AI solutions." />
        <meta name="keywords" content="enterprise AI, open source AI, AI independence, vendor lock-in, AI strategy, internal AI capabilities" />
        <meta property="og:title" content="AI Clean Core - Enterprise AI Independence" />
        <meta property="og:description" content="AI isn't optional—it's survival. Build internal AI capabilities with open-source solutions." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/homepage" />
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        
        <main className="pt-16 lg:pt-20">
          {/* Hero Section - Bold executive-focused opening */}
          <HeroSection />

          {/* Value Proposition - Three pillars of AI independence */}
          <ValueProposition />

          {/* AI Readiness Widget - Interactive engagement tool */}
          <AIReadinessWidget />

          {/* Client Metrics - Proven results and transformation stories */}
          <ClientMetrics />

          {/* Role-Based Navigation - Personalized user journeys */}
          <RoleBasedNavigation />

          {/* Social Proof - Client testimonials and trust indicators */}
          <SocialProof />
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">AI Clean Core</div>
                    <div className="text-sm text-gray-400">Enterprise AI Independence</div>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                  Helping organizations reclaim control over their AI destiny through open-source solutions and internal capability building.
                </p>
                <div className="text-sm text-gray-400">
                  © {new Date()?.getFullYear()} AI Clean Core. All rights reserved.
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Solutions</h3>
                <ul className="space-y-2 text-gray-300">
                  <li><a href="/ai-core-blueprint" className="hover:text-brand-secondary transition-colors">AI Core Blueprint</a></li>
                  <li><a href="/open-source-advantage" className="hover:text-brand-secondary transition-colors">Open-Source Advantage</a></li>
                  <li><a href="/services-implementation" className="hover:text-brand-secondary transition-colors">Implementation Services</a></li>
                  <li><a href="/case-studies-proof" className="hover:text-brand-secondary transition-colors">Case Studies</a></li>
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Resources</h3>
                <ul className="space-y-2 text-gray-300">
                  <li><a href="/resources-hub" className="hover:text-brand-secondary transition-colors">Resource Hub</a></li>
                  <li><a href="/get-started" className="hover:text-brand-secondary transition-colors">Get Started</a></li>
                  <li><a href="/about-ai-clean-core" className="hover:text-brand-secondary transition-colors">About Us</a></li>
                  <li><a href="#" className="hover:text-brand-secondary transition-colors">Contact</a></li>
                </ul>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Homepage;